// The Breaks report: the pure part. Rows come from the branch summary's `breaks`.
import type { BreakRow } from '../lib/types'

export type BreakKind = 'lunch' | 'short_break' | 'task'
export const BREAK_KINDS: readonly BreakKind[] = ['lunch', 'short_break', 'task']

/** One break that ran, at one branch. */
export interface BreakLine {
  branchId: string
  branch: string
  zone?: string
  employee: string
  counter: string
  kind: BreakKind
  reason: string
  /** A branch's own reason, which other branches do not have. */
  branchOnly?: boolean
  start: string
  end?: string
  seconds: number
  /** Who approved it; empty when it started without a decision. */
  approver: string
  auto: boolean
}

const isKind = (k: string): k is BreakKind => (BREAK_KINDS as readonly string[]).includes(k)

/** A branch's breaks that ran: closes, and requests that never started a break, are left out. */
export function breakLines(rows: readonly BreakRow[], branch: { id: string; name: string; timezone?: string }): BreakLine[] {
  return rows.flatMap((r) =>
    isKind(r.kind) && r.startedAt && (r.state === 'approved' || r.state === 'auto')
      ? [
          {
            branchId: branch.id,
            branch: branch.name,
            zone: branch.timezone || undefined,
            employee: r.staffName,
            counter: r.counterCode,
            kind: r.kind,
            reason: r.reason ?? '',
            branchOnly: r.reasonBranchOnly || undefined,
            start: r.startedAt,
            end: r.endedAt,
            seconds: r.seconds,
            approver: r.state === 'auto' ? '' : (r.decidedByName ?? ''),
            auto: r.state === 'auto',
          },
        ]
      : [],
  )
}

/** Newest first; ties by employee. */
export function newestFirst(lines: readonly BreakLine[]): BreakLine[] {
  return [...lines].sort((a, b) => Date.parse(b.start) - Date.parse(a.start) || a.employee.localeCompare(b.employee))
}

export interface BreakFilter {
  employee: string
  kind: BreakKind | ''
}

export const filterLines = (lines: readonly BreakLine[], f: BreakFilter) =>
  lines.filter((l) => (!f.employee || l.employee === f.employee) && (!f.kind || l.kind === f.kind))

/** The employees the lines name, A–Z, once each. */
export const employeesOf = (lines: readonly BreakLine[]) => [...new Set(lines.map((l) => l.employee))].sort((a, b) => a.localeCompare(b))

export interface BreakTotals {
  count: number
  seconds: number
  byKind: Record<BreakKind, number>
}

export function breakTotals(lines: readonly BreakLine[]): BreakTotals {
  const byKind: Record<BreakKind, number> = { lunch: 0, short_break: 0, task: 0 }
  let seconds = 0
  for (const l of lines) {
    byKind[l.kind] += l.seconds
    seconds += l.seconds
  }
  return { count: lines.length, seconds, byKind }
}

const WALL = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } as const

/** A moment as "YYYY-MM-DD HH:MM" on the branch's clock (the reader's when the zone is unknown). */
export function wallTime(iso: string, zone?: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  let parts: Intl.DateTimeFormatPart[]
  try {
    parts = new Intl.DateTimeFormat('en-GB', { ...WALL, timeZone: zone }).formatToParts(d)
  } catch {
    parts = new Intl.DateTimeFormat('en-GB', WALL).formatToParts(d)
  }
  const p = (t: string) => parts.find((x) => x.type === t)?.value ?? ''
  return `${p('year')}-${p('month')}-${p('day')} ${p('hour')}:${p('minute')}`
}

/** Seconds as minutes to one decimal, as the ticket export's Breaks sheet writes them. */
export const minutesOf = (sec: number) => Math.round(sec / 6) / 10

/** The export's rows after its header: times on the branch's clock, length in minutes. */
export function breakRows(
  lines: readonly BreakLine[],
  kindName: (k: BreakKind) => string,
  automatic: string,
  branchOnly = 'branch only',
): (string | number)[][] {
  return lines.map((l) => [
    l.branch,
    l.employee,
    l.counter,
    kindName(l.kind),
    l.branchOnly && l.reason ? `${l.reason} (${branchOnly})` : l.reason,
    wallTime(l.start, l.zone),
    l.end ? wallTime(l.end, l.zone) : '',
    minutesOf(l.seconds),
    l.auto ? automatic : l.approver,
  ])
}

/** RFC 4180 text, with a BOM so Excel reads it as UTF-8. */
export function toCsv(table: readonly (readonly (string | number)[])[]): string {
  const cell = (v: string | number) => {
    const s = String(v)
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return '﻿' + table.map((r) => r.map(cell).join(',')).join('\r\n') + '\r\n'
}
